import React from "react";

/**
 * Renders a structured letter (from LetterAction's letter_data) as an
 * actual letterhead-styled document instead of a plain chat bubble.
 *
 * Expects letterData shaped like:
 * {
 *   company_name, date, subject, salutation,
 *   body: [ "paragraph 1", "paragraph 2", ... ],
 *   closing, signature_name, signature_title,
 *   employee: { name, employee_id, designation, department }
 * }
 */
export default function LetterCard({ letterData, onDownloadPdf, onCopy }) {
  if (!letterData) return null;

  const {
    company_name,
    date,
    subject,
    salutation,
    body = [],
    closing,
    signature_name,
    signature_title,
    employee,
  } = letterData;

  return (
    <div style={styles.wrapper}>
      <div style={styles.actionsRow}>
        {onCopy && (
          <button style={styles.actionBtn} onClick={onCopy} type="button">
            Copy
          </button>
        )}
        {onDownloadPdf && (
          <button style={styles.actionBtnPrimary} onClick={onDownloadPdf} type="button">
            Download PDF
          </button>
        )}
      </div>

      <div style={styles.letter}>
        <div style={styles.letterhead}>
          <div style={styles.companyName}>{company_name}</div>
        </div>

        <div style={styles.date}>{date}</div>

        {employee && (
          <div style={styles.toBlock}>
            <div>{employee.name}</div>
            <div style={styles.muted}>
              Employee ID: {employee.employee_id}
              {employee.designation ? ` · ${employee.designation}` : ""}
              {employee.department ? ` · ${employee.department}` : ""}
            </div>
          </div>
        )}

        {subject && <div style={styles.subject}>Subject: {subject}</div>}

        {salutation && <div style={styles.salutation}>{salutation}</div>}

        {body.map((paragraph, i) => (
          <p key={i} style={styles.paragraph}>
            {paragraph}
          </p>
        ))}

        <div style={styles.signatureBlock}>
          <div>{closing}</div>
          <div style={{ marginTop: 32 }}>{signature_name}</div>
          <div style={styles.muted}>{signature_title}</div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    maxWidth: 560,
    margin: "8px 0",
  },
  actionsRow: {
    display: "flex",
    gap: 8,
    marginBottom: 8,
    justifyContent: "flex-end",
  },
  actionBtn: {
    padding: "6px 14px",
    fontSize: 13,
    border: "1px solid #d0d5dd",
    borderRadius: 6,
    background: "#fff",
    color: "#344054",
    cursor: "pointer",
  },
  actionBtnPrimary: {
    padding: "6px 14px",
    fontSize: 13,
    border: "none",
    borderRadius: 6,
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
  },
  letter: {
    background: "#fff",
    border: "1px solid #e4e7ec",
    borderRadius: 10,
    padding: "28px 32px",
    boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
    fontFamily: "Georgia, 'Times New Roman', serif",
    color: "#1d2939",
    lineHeight: 1.6,
  },
  letterhead: {
    borderBottom: "2px solid #2563eb",
    paddingBottom: 12,
    marginBottom: 20,
  },
  companyName: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  date: {
    fontSize: 13,
    color: "#667085",
    marginBottom: 18,
  },
  toBlock: {
    marginBottom: 18,
    fontSize: 14,
  },
  muted: {
    color: "#667085",
    fontSize: 12.5,
  },
  subject: {
    fontWeight: 700,
    marginBottom: 16,
    fontSize: 14.5,
  },
  salutation: {
    marginBottom: 12,
  },
  paragraph: {
    margin: "0 0 14px 0",
    fontSize: 14.5,
    textAlign: "justify",
  },
  signatureBlock: {
    marginTop: 24,
    fontSize: 14.5,
  },
};
